/**
 * «Hvor mange så det» in the panel.  Meeting notes 2026-10-31 (spec 01 C).
 *
 * One hook and one small line, for «Varsler» (sent pushes, the announcements up now), «Bannere» and «Arrangementer».
 * The numbers come from GET /admin/views (view_mark, migration 073): how many signed-in app users opened or saw a thing
 * — a number, never who. Hidden altogether while the mosque does not have `view_counts` («Visningstall», its own
 * privacy scope) or this staff member may not read that kind; a failed ask hides it too (the count is extra, never a
 * reason for an error on the page).
 */
import React, { useEffect, useState } from 'react';
import { can, send } from '../lib/api';
import { useNav } from '../lib/nav';
import { seenIds, seenShown, type SeenKind } from '../lib/seen';

/**
 * The viewers per id, or null while not shown / not known. `days`: only the last N days (banners: 30). The ids are asked
 * for once per change of the set, at most MAX_IDS (the newest first is the caller's order).
 */
export function useSeenCounts(kind: SeenKind, ids: (string | null | undefined)[], days?: number): Record<string, number> | null {
  const { caps } = useNav();
  const shown = seenShown(caps, kind, can);
  const key = seenIds(ids).join(',');
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (!shown || !key) { setCounts(null); return; }
    let live = true;
    void send(`/views?kind=${kind}&ids=${encodeURIComponent(key)}${days ? `&days=${days}` : ''}`).then((r) => {
      if (!live) return;
      if (!r.ok || !r.body || typeof r.body.counts !== 'object') { setCounts(null); return; }
      const out: Record<string, number> = {};
      for (const [id, c] of Object.entries(r.body.counts as Record<string, { viewers?: number }>)) out[id] = Number(c && c.viewers) || 0;
      setCounts(out);
    });
    return () => { live = false; };
  }, [shown, kind, key, days]);
  return shown ? counts : null;
}

const fmt = (n: number) => n.toLocaleString('nb-NO');

/** «Åpnet av 12» — nothing while the counts are hidden or not known for this id. `block`: a line of its own. */
export function SeenCount({ counts, id, label, suffix, block = false }: {
  counts: Record<string, number> | null; id: string | null | undefined; label: string; suffix?: string; block?: boolean;
}) {
  if (!counts || !id || !(id in counts)) return null;
  const Tag = block ? 'div' : 'span';
  return (
    <Tag className={block ? 'sub seen-count' : 'note seen-count'} title="Antall innloggede i appen — bare et tall, aldri hvem.">
      {label} {fmt(counts[id])}{suffix ? ' ' + suffix : ''}
    </Tag>
  );
}
